// ============================================
// UBUS V2.1 共享包 - 导出入口
// ============================================

export type {
  Vehicle,
  VehicleFeature,
  Driver,
  Trip,
  TripSource,
  TripAssignmentStatus,
  LocationLog,
  SOSEvent,
  Organization,
  OrganizationCustomerType,
  FleetOperator,
  ReminderType,
  ReminderConfig,
  ReminderEvent,
  ReminderDelivery,
  ReminderRead,
  ReminderSourceFieldRow,
  ReminderChannel,
  ReminderRuleKind,
  ReminderCategory,
  MessageTemplate,
  MessageTemplateChannel,
  TripFeedback,
  VehicleInspection,
  InspectionSeverityResult,
  InspectionChecklistItemRow,
  InspectionBodyDefect,
  InspectionWorkFields,
  FuelExpense,
  SOSFormData,
  TripDocument,
  RescueVehicleRecommendation,
  DispatchRecommendation,
  Job,
  JobLeg,
  JobType,
  JobStatus,
  JobLegType,
  JobSource,
  JobAcceptanceStatus,
  JobAssistStatus,
  JobLifecycleEvent,
  JobLifecycleEventType,
} from './types/database';

export { VEHICLE_FEATURE_OPTIONS, ORGANIZATION_CUSTOMER_TYPES } from './types/database';

export {
  formatVehiclePublicLabel,
  formatVehicleAdminLabel,
} from './utils/vehicleLabels';

export type {
  ComplianceReadiness,
  ComplianceSummary,
} from './utils/complianceReadiness';
export {
  COMPLIANCE_DUE_SOON_DAYS,
  addLocalDays,
  complianceTagColor,
  complianceReadinessRank,
  driverComplianceReadiness,
  isExpiryPast,
  isDispatchBlockedByExpiry,
  parseComplianceDispatchError,
  summarizeActiveCompliance,
  toLocalDateString,
  vehicleComplianceReadiness,
} from './utils/complianceReadiness';
export type { ComplianceDispatchErrorKind } from './utils/complianceReadiness';

export {
  hasFullCrew,
  resolveTripStatusAfterAssign,
  formatCrewAssignmentLabelEn,
  formatCrewAssignmentLabelZh,
} from './utils/crewAssignment';

export { supabase } from './config/supabase';

export { AuthGuard } from './components/AuthGuard';
export { EmailLoginPage } from './components/EmailLoginPage';
export type { EmailLoginPageProps, EmailLoginPageLabels } from './components/EmailLoginPage';
export { ResetPasswordPage } from './components/ResetPasswordPage';
export type { ResetPasswordPageProps, ResetPasswordPageLabels } from './components/ResetPasswordPage';
export { ChangePasswordForm } from './components/ChangePasswordForm';
export type {
  ChangePasswordFormProps,
  ChangePasswordFormLabels,
} from './components/ChangePasswordForm';
export { SetupCredentialsNotice } from './components/SetupCredentialsNotice';
export type { SetupCredentialsNoticeProps } from './components/SetupCredentialsNotice';
export { TripMapView } from './components/TripMapView';
export type { TripMapViewHandle, TripMapViewProps } from './components/TripMapView';
export { default as GpsTrackHistoryList } from './components/GpsTrackHistoryList';
export type { GpsTrackHistoryListProps } from './components/GpsTrackHistoryList';
export { AddressField } from './components/AddressField';
export type {
  AddressFieldProps,
  AddressCoordinates,
  AddressGeocodeStatus,
  AddressFieldVariant,
} from './components/AddressField';
export { AddressMapPreview } from './components/AddressMapPreview';
export type { AddressMapPreviewProps } from './components/AddressMapPreview';
export { parseUserRole, phoneToAuthEmail, isAdminRole, isFleetOwnerRole } from './auth/roles';
export type { UserRole } from './auth/roles';
export {
  PORTAL_PERMISSIONS,
  ROUTE_VIEW_PERMISSION,
  hasPermission,
  viewPermissionForPath,
} from './auth/permissions';
export type {
  PortalPermission,
  PortalAccess,
  PortalAccessRole,
} from './auth/permissions';
export { usePortalPermissions } from './auth/usePortalPermissions';
export type { UsePortalPermissionsResult } from './auth/usePortalPermissions';
export { PermissionGate } from './components/PermissionGate';
export type { PermissionGateProps } from './components/PermissionGate';
export { provisionAuthUser } from './auth/provisionUser';
export type { ProvisionAuthResult } from './auth/provisionUser';
export {
  useSupabaseSession,
  useAuthUser,
  signOut,
} from './auth/session';
export {
  signInWithEmailPassword,
  signInWithPhonePassword,
  getAuthErrorCode,
  getFriendlyAuthError,
} from './auth/login';
export type { AuthErrorCode, SignInResult } from './auth/login';
export {
  requestPasswordReset,
  updatePassword,
  changePassword,
  resetPasswordRedirectUrl,
  driverResetPasswordRedirectUrl,
  clientResetPasswordRedirectUrl,
  sendPasswordSetupLink,
  logAssistResetAudit,
  PASSWORD_RESET_REQUEST_ACK,
  PASSWORD_RESET_REQUEST_ACK_ZH,
} from './auth/passwordRecovery';
export type { SendPasswordSetupParams, SendPasswordSetupResult } from './auth/passwordRecovery';

import { supabase } from './config/supabase';

export {
  validatePhone,
  formatPhoneToInternational,
  validateSOSForm,
} from './utils/validators';

export {
  findTripAssignmentConflicts,
  formatTripConflictMessage,
} from './utils/tripConflict';
export type { TripConflict } from './utils/tripConflict';
export { isDriverBlockedByFullDayEscort } from './services/fullDayEscort';
export {
  scheduleWindowsOverlap,
  fitsInIdleGap,
  defaultEndTimeFromPickup,
  timeStringToMinutes,
  minutesToTimeString,
} from './utils/scheduleConflict';
export type { ScheduleWindow } from './utils/scheduleConflict';
export {
  checkVehicleScheduleConflict,
  checkDriverScheduleConflict,
  formatScheduleConflictMessage,
} from './services/fleetSchedule';
export {
  isDriverVisibleTrip,
  isGpsTrackableTrip,
  formatTripSourceLabel,
  formatAssignmentStatusLabel,
  formatClientTripDisplayStatus,
  getClientTripDisplayColor,
  DRIVER_VISIBLE_TRIP_OR_FILTER,
} from './utils/driverTripVisibility';
export {
  mapJobToClientRequestStatus,
  formatClientRequestStatus,
  getClientRequestStatusColor,
  civilDateInTimeZone,
  civilMinutesInTimeZone,
  isPastSchedulePickup,
  isClientRequestOverdueUnscheduled,
  isJobOverdueUnscheduled,
} from './utils/clientRequestStatus';
export type { ClientRequestStatus } from './utils/clientRequestStatus';
export { parseClientRequestNotes, composeClientRequestLegNotes } from './utils/clientRequestNotes';
export {
  composeTripSpecialNotes,
  fetchComposeTripNotesFromIds,
} from './utils/composeTripSpecialNotes';
export type {
  ClientPassengerGroup,
  ParsedClientRequestNotes,
} from './utils/clientRequestNotes';
export { useFleetOperator } from './hooks/useFleetOperator';
export {
  recommendFleetOperatorsForTrip,
  assignPlatformTripToOwner,
  acceptPlatformTrip,
  rejectPlatformTrip,
  formatFleetOperatorRecommendation,
} from './services/fleetOperatorDispatch';
export type { FleetOperatorRecommendation } from './services/fleetOperatorDispatch';
export {
  setAcceptingTasks,
  getCurrentPositionCoords,
} from './services/acceptingTasks';
export type { SetAcceptingTasksResult } from './services/acceptingTasks';
export {
  requestJobPlatformAssist,
  cancelJobPlatformAssist,
  reassignJobFleetFromAssist,
  recommendFleetOperatorsForJob,
  listJobLifecycleEvents,
  isJobWritableForFleet,
} from './services/jobPlatformAssist';
export { deleteFleetOperator } from './services/deleteFleetOperator';
export {
  filterSourcesByCategory,
  findSourceField,
  isDriverInAppSourceRow,
  slugifyReminderCode,
  subjectLabelFromSource,
  isSafeSqlIdent,
} from './reminders/allowlist';
export type { ReminderSourceField, ReminderSourceAllowlistEntry } from './reminders/allowlist';
export {
  fetchVehicleAvailability,
  summarizeVehicleAvailability,
  formatAvailabilityStatusLabel,
  getAvailabilityStatusColor,
} from './services/fleetAvailability';
export type {
  VehicleAvailabilityRow,
  VehicleAvailabilityStatus,
  VehicleAvailabilitySummary,
} from './services/fleetAvailability';
export {
  fetchLatestVehiclePositions,
  countOnlineVehicles,
  fleetPositionsToMapMarkers,
  FLEET_ONLINE_MINUTES,
} from './services/fleetVehiclePositions';
export type {
  FleetVehiclePosition,
  FleetMapDisplayStatus,
  FetchLatestVehiclePositionsOptions,
} from './services/fleetVehiclePositions';

export type {
  ScheduleLegInput,
  ChainTimelineEntry,
  ChainValidationResult,
  TravelTimeOptions,
  VehicleChainAssignment,
  ScheduleAssignmentCheck,
  ScheduleJobInput,
  ScheduleVehicleInput,
  VehicleChainPlan,
  DailySchedulePlan,
  PlanDailyScheduleInput,
  SchedulePlanPublishPayload,
} from './scheduling';
export {
  estimateTravelMinutes,
  estimateStaticTravelMinutes,
  estimateTravelKm,
  parseSchedulingSettings,
  schedulingSettingsToTravelOptions,
  DEFAULT_SCHEDULING_SETTINGS,
  routeOdKey,
  collectDeadheadOdPairs,
  prefetchDirectionsTravelMinutes,
  validateVehicleChain,
  jobLegOccupancyEndMinutes,
  buildClientTransferLegTimes,
  occupancyEndTimeString,
  checkScheduleAssignments,
  findDuplicateJobAssignments,
  findUnassignedJobs,
  collectLegJobIds,
  planDailySchedule,
  planToPublishPayload,
  countPublishableJobs,
  assignJobToVehicle,
  reassignJobToVehicle,
  unassignJobFromPlan,
  jobLegsToScheduleInputs,
  tripToFixedScheduleLeg,
  groupFixedLegsByVehicle,
  planNewAssignmentWindows,
  planNewDriverAssignmentWindows,
  findIntraPlanDriverConflicts,
  enrichDuplicateAssignments,
  actionableDuplicateJobs,
  publishableJobIds,
  jobsMissingOrgForHqPublish,
  classifyPublishScheduleError,
  formatPublishScheduleError,
  applyJobOrgToPlan,
  suggestVehiclesForJob,
  listScheduleFixItems,
  listScheduleFixReport,
  isSchedulePublishChainOk,
  suggestSplitVehicleCount,
  validateSplitPassengerCounts,
  seedSplitPassengerCounts,
  formatIssueEnglish,
  formatIssuesEnglish,
  draftStorageKey,
  serializeDailyScheduleDraft,
  parseDailyScheduleDraft,
  pinJobOnPlan,
  unpinJobOnPlan,
  SCHED_FIXTURE_MANUAL_PLAN,
  SCHED_FIXTURE_ALL_JOB_IDS,
  buildAbcdPlanningJobs,
} from './scheduling';
export type {
  SchedulingSettings,
  MutatePlanResult,
  DuplicateAssignmentDetail,
  DuplicateAssignmentKind,
  MissingOrgJob,
  PublishScheduleError,
  PublishScheduleErrorKind,
  ApplyJobOrgResult,
  VehicleSuggestion,
  ScheduleFixVehicleItem,
  ScheduleFixJobItem,
  ScheduleFixReport,
  SplitCountsValidation,
  ScheduleValidationIssue,
  ScheduleValidationCode,
  DailyScheduleDraft,
  SchedulePinFailure,
} from './scheduling';
export {
  fetchDailyScheduleContext,
  planDailyScheduleForDate,
  publishSchedulePlan,
  assertPlanNoVehicleConflicts,
  assertPlanNoDriverConflicts,
  DEFAULT_CHAIN_BUFFER_MINUTES,
} from './services/schedulePlanning';
export type { DailyScheduleContext, PublishScheduleResult } from './services/schedulePlanning';
export {
  startOfIsoWeek,
  endOfIsoWeek,
  eachDateInclusive,
  hasLocalScheduleDraft,
  fetchScheduleOverviewRange,
  fetchScheduleOverviewIsoWeek,
  fetchScheduleOverviewDayDetail,
} from './services/scheduleOverview';
export type {
  ScheduleDaySummary,
  ScheduleOverviewRange,
  OverviewTripBlock,
  OverviewUnscheduledJob,
  OverviewVehicleRow,
  ScheduleOverviewDayDetail,
} from './services/scheduleOverview';
export {
  computeTripRouteEta,
  applyTripRouteEta,
  refreshTripRouteEtaById,
  refreshTripsRouteEtaForDate,
  estimatedDropoffFromPickup,
} from './services/tripRouteEta';
export type {
  RouteDriveSource,
  TripRouteEtaFields,
} from './services/tripRouteEta';
export {
  buildTripDepartureIso,
  classifyDirectionsRouting,
  resolveRouteDriveSourceAfterAttempt,
  DEFAULT_TRIP_TIME_ZONE,
} from './services/tripDeparture';
export {
  routeDriveSourceI18nKey,
  routeDriveSourceTagColor,
  formatRouteDriveKm,
} from './services/routeDriveDisplay';
export { splitOversizedJob } from './services/splitOversizedJob';
export type { SplitOversizedJobResult } from './services/splitOversizedJob';

export {
  scoreCapacityFit,
  scoreDistance,
  scoreRequirements,
  computeTotalDispatchScore,
  formatDispatchBreakdown,
} from './utils/dispatchScore';
export type { RequirementsContext, DispatchScoreBreakdown } from './utils/dispatchScore';

export {
  recommendDispatchAssignments,
} from './services/dispatchRecommendation';

export {
  canTransition,
  canEditTrip,
  canCancelTrip,
  getAllowedNextStatuses,
  shouldAutoDispatch,
  shouldDemoteToScheduled,
} from './utils/tripStatus';
export type { TripStatus } from './utils/tripStatus';

export { useRealtimeChannel } from './hooks/useRealtimeChannel';
export type { RealtimeSubscription } from './hooks/useRealtimeChannel';

export { compressImage, validateImageFile } from './utils/imageCompress';

export { mapSOSPriority, sosPriorityColor, sosPriorityLabel } from './utils/sosPriority';
export type { SOSPriority } from './utils/sosPriority';

export {
  generateTripDocument,
  fetchOrgDocuments,
  recommendRescueVehicles,
  fetchExpiringCredentials,
} from './services/documents';
export type { TripDocumentType, GenerateDocumentResult } from './services/documents';

export {
  formatDate,
  formatDateTime,
  formatTime,
  configureFormatDefaults,
  getFormatDefaults,

  formatPhone,
  formatCapacity,
  formatStatus,
  getStatusColor,
  parseGeoPoint,
  toGeoPointWKT,
  formatETA,
  formatDistance,
  formatTrackTimestamp,
  formatTrackTimeLabel,
  buildGoogleMapsNavUrl,
} from './utils/formatters';
export type { DateFormatOptions } from './utils/formatters';

export {
  geocodeAddress,
  resolveTripGeopoints,
  resolveTripGeopointsStrict,
  backfillTripGeopoints,
  TripGeocodeError,
  parseGeocodeResponse,
} from './utils/geocode';
export type {
  AddressInput,
  TripGeopointsResult,
  GeocodeFieldFailure,
  GeocodeField,
} from './utils/geocode';
export { cancelTrip, fetchSystemSettings } from './utils/tripActions';
export { formatUserFacingDbError } from './utils/formatUserFacingDbError';
export { submitSOSEvent } from './services/sosEvents';
export type { SubmitSOSEventParams } from './services/sosEvents';

export {
  CCI_CHECKLIST_ITEMS,
  CCI_AFTER_CHECKLIST_ITEMS,
  CCI_CRITICAL_ITEM_IDS,
  CCI_CHECKLIST_GROUPS,
  CCI_BODY_VIEWS,
  CCI_DECLARATION_TEXT,
  CCI_DECLARATION_TEXT_VER,
  checklistItemLabel,
  countFailedItems,
  itemsAnsweredCount,
  evaluateInspection,
  assertDepartAllowed,
  isAfterChecklistComplete,
  expandAfterChecklistItems,
  expandBeforeChecklistItems,
  expandChecklistAgainstCatalog,
  FITNESS_FORM_VER,
  FITNESS_QUESTION_IDS,
  CCI_FITNESS_DECLARATION_TEXT_VER,
  emptyFitnessAnswers,
  parseFitnessAnswers,
  evaluateFitness,
  isFitnessPassing,
  buildInspectionPdfBlob,
  downloadInspectionPdf,
  downloadBlob,
  inspectionPdfFilename,
} from './inspection';
export type {
  ChecklistItemStatus,
  ChecklistSeverity,
  ChecklistItemDef,
  ChecklistGroupId,
  BodyView,
  InspectionItemState,
  SeverityResult,
  EvaluateInspectionInput,
  EvaluateInspectionResult,
  ShiftInspectionGateRecord,
  DepartBlockReason,
  AssertDepartAllowedInput,
  AssertDepartAllowedResult,
  FitnessQuestionId,
  FitnessAnswers,
  EvaluateFitnessResult,
  InspectionPdfSource,
  ChecklistViewStatus,
  ChecklistViewRow,
} from './inspection';

export type {
  LatLng,
  MapAdapter,
  MapMarker,
  MapMarkerRole,
  MapProvider,
  MapSettings,
  RouteResult,
  TrackPoint,
} from './maps';
export {
  createMapAdapter,
  fetchMapSettings,
  getActiveMapApiKey,
  isMapConfigured,
  haversineKm,
  formatKm,
  reverseGeocodeClient,
} from './maps';
export {
  resolveTripMapPathDisplay,
  locationLogsToTrack,
  locationLogsToTrackPoints,
  buildTripMapMarkers,
  describeRoutePlanningError,
} from './maps/tripMapPaths';
export type { TripMapPathDisplay } from './maps/tripMapPaths';
export {
  PICKUP_MARKER_ICON_URL,
  DROPOFF_MARKER_ICON_URL,
  isStopMarkerRole,
  stopMarkerIconUrl,
} from './maps/markerIcons';

export const SOS_EVENT_TYPES = [
  'Vehicle Breakdown',
  'Traffic Accident',
  'Passenger Emergency',
  'Vehicle Not Appearing',
  'Other',
] as const;

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function getCurrentOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('organizations')
    .select('id')
    .ilike('contact_email', user.email ?? '')
    .maybeSingle();

  if (error || !data) return null;
  return data.id;
}

export async function uploadFile(
  bucket: string,
  path: string,
  file: File,
  options?: { cacheControl?: string; upsert?: boolean }
): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.error('Upload error: not authenticated');
    return null;
  }

  const scopedPath = path.includes('/') && path.startsWith(`${user.id}/`)
    ? path
    : `${user.id}/${path}`;

  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(scopedPath, file, {
      cacheControl: options?.cacheControl || '3600',
      upsert: options?.upsert || false,
    });

  if (error) {
    console.error('Upload error:', error);
    return null;
  }

  if (bucket === 'vehicles') {
    const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(data.path);
    return publicUrl;
  }

  // Private buckets (drivers, sos, uploads) store path for signed URL retrieval
  return data.path;
}

export async function getSignedUrl(
  bucket: string,
  path: string,
  expiresIn: number = 60
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    console.error('Get signed URL error:', error);
    return null;
  }

  return data.signedUrl;
}

// ============================================
// Observability (Sentry + request correlation)
// ============================================

export {
  bootstrapObservability,
  initSentry,
  isSentryEnabled,
  captureException,
  setSentryUser,
  installGlobalErrorHandlers,
  AppErrorBoundary,
  invokeEdgeFunction,
  createRequestId,
  logClientEvent,
} from './observability';
export type {
  PortalApp,
  AppErrorBoundaryProps,
  InvokeEdgeFunctionOptions,
  InvokeEdgeFunctionResult,
} from './observability';
